import { Skeleton } from 'antd';
import { ROUTES } from 'common/constants';
import { usePermissionsBalance } from 'common/hooks/use-permissions-balance.hook';
import { ActionRbacEnum } from 'common/utils/auth-provider.util';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import EntitlementEnum from './entitlements.enum';
import { useFarm } from './farm/farm.provider';
import { useOrganization } from './organization.provider';

interface RequireOrganizationProps {
  children: JSX.Element;
  permissionScreen?: ActionRbacEnum;
  entitlementScreen?: EntitlementEnum;
}

const RequireOrganization = ({
  children,
  permissionScreen,
  entitlementScreen,
}: RequireOrganizationProps) => {
  const {
    organizations,
    entitlements,
    currentOrganization,
    setCurrentOrganizationId,
    isLoading: isLoadingOrganization,
    isFetched: isFetchedOrganization,
    plan,
    licensing,
  } = useOrganization();
  const {
    farms,
    setCurrentFarmId,
    isLoading: isFarmLoading,
    isFetched: isFetchedFarms,
  } = useFarm();
  const { permissions } = usePermissionsBalance();
  const { blockFmsMaintenance } = useFlags<{
    blockFmsMaintenance: string;
  }>();

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsOrganizationId = searchParams.get('organizationId');
  const searchParamsFarmId = searchParams.get('farmId');

  const isLoading = useMemo(
    () => isLoadingOrganization || isFarmLoading,
    [isLoadingOrganization, isFarmLoading],
  );
  const isFetched = useMemo(
    () => isFetchedOrganization && !isLoading,
    [isFetchedOrganization, isLoading],
  );

  const isOrganizationsMissing = useMemo(
    () => isFetched && !organizations.length,
    [isFetched, organizations.length],
  );

  const isSearchParamsOrganizationMissing = useMemo(
    () =>
      searchParamsOrganizationId &&
      isFetched &&
      !organizations.some(
        (organization) => organization.id === searchParamsOrganizationId,
      ),
    [organizations, searchParamsOrganizationId, isFetchedOrganization],
  );

  const isCurrentOrganizationMissing = useMemo(
    () => isFetched && !currentOrganization && !searchParamsOrganizationId,
    [isLoading, currentOrganization, searchParamsOrganizationId],
  );

  // Legacy links (e.g. bookmarks from the removed /organization screen) may
  // carry a farm name in the farmId param instead of a real id. Only trust it
  // once the farms are loaded and it matches one of them.
  const isSearchParamsFarmMissing = useMemo(
    () =>
      !!searchParamsFarmId &&
      isFetchedFarms &&
      !farms.some(
        (farm) =>
          farm.id === searchParamsFarmId ||
          farm.externalId === searchParamsFarmId,
      ),
    [farms, searchParamsFarmId, isFetchedFarms],
  );

  useEffect(() => {
    if (searchParamsOrganizationId && !isSearchParamsOrganizationMissing) {
      setCurrentOrganizationId(searchParamsOrganizationId);
    }
  }, [
    searchParamsOrganizationId,
    isSearchParamsOrganizationMissing,
    setCurrentOrganizationId,
  ]);

  useEffect(() => {
    if (!searchParamsFarmId) {
      return;
    }

    if (isSearchParamsFarmMissing) {
      // Drop the stale value so it never reaches the API and stops being
      // carried over by useNavigateWithOrgAndFarm. The org-level view is a
      // valid state, so the user stays on the page they asked for.
      setCurrentFarmId(null);

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('farmId');
      setSearchParams(nextSearchParams, { replace: true });
      return;
    }

    setCurrentFarmId(searchParamsFarmId);
  }, [
    searchParamsFarmId,
    isSearchParamsFarmMissing,
    setCurrentFarmId,
    searchParams,
    setSearchParams,
  ]);

  if (blockFmsMaintenance) {
    return (
      <Navigate to={ROUTES.MAINTENANCE} state={{ from: location }} replace />
    );
  }

  if (isOrganizationsMissing) {
    return (
      <Navigate
        to={ROUTES.NO_ORGANIZATION}
        state={{ from: location }}
        replace
      />
    );
  }

  if (isFetched) {
    if (isCurrentOrganizationMissing || isSearchParamsOrganizationMissing) {
      return (
        <Navigate to={ROUTES.MY_CROPWISE} state={{ from: location }} replace />
      );
    }
  }

  const hasPermissions = permissions.length > 0;
  const hasEntitlements = entitlements.length > 0;

  const lacksRequiredPermission =
    permissionScreen &&
    hasPermissions &&
    !permissions.includes(permissionScreen);
  const lacksRequiredEntitlement =
    entitlementScreen &&
    hasEntitlements &&
    !entitlements.includes(entitlementScreen);

  if (lacksRequiredPermission || lacksRequiredEntitlement) {
    return (
      <Navigate to={ROUTES.NO_PERMISSION} state={{ from: location }} replace />
    );
  }

  const lacksBalanceAccess =
    hasPermissions && !permissions.includes(ActionRbacEnum.ACCESS_BALANCE);
  // Plan is considered expired if:
  // 1. plan is null (expired)
  // 2. licensing data exists (user has/had a subscription)
  // 3. current organization exists (valid context)
  const hasPlanExpired = plan === null && !!licensing && !!currentOrganization;

  if (lacksBalanceAccess || hasPlanExpired) {
    return (
      <Navigate
        to={ROUTES.NO_PERMISSION_BALANCE}
        state={{ from: location }}
        replace
      />
    );
  }

  const isStillLoading =
    isLoading || permissions === undefined || entitlements === undefined;

  return isStillLoading ? <Skeleton active /> : children;
};

export { RequireOrganization };
